import TimelineIcon from '@mui/icons-material/Timeline';
import { Box, IconButton } from '@mui/material';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useUpdateCompanyStates } from '../../../../services/hooks/useUpdateCompanyAndState';
import { MaggieFeatureFlags } from '../../../../util/feature-flags';
import { IFormField } from '../../../../view-models/form.view-model';
import { BasicDialog } from '../../../Dialog/BasicDialog';
import { GenericFallbacksWrapper } from '../../../Fallback/GenericFallbacksWrapper';
import { TimeSeriesDataTable, TimeSeriesDataTableEditable } from './TimeSeriesDataTable';

export function FieldWithTimeSeriesButton({ formField }: { formField: IFormField<unknown> }) {
  const { entityId, customFieldId } = useCustomFieldDataValues(formField.key);
  const [open, setOpen] = useState(false);
  const { showEditTimeSeries } = useFlags<MaggieFeatureFlags>();
  const updateCompanyStates = useUpdateCompanyStates();
  const onClose = useCallback(() => {
    if (
      showEditTimeSeries &&
      entityId
    ) // not ideal - we should be refetching company only if data changed, but setting companyStateJ triggers
    // suspense, which breaks the grid (can keep separate state to see if data changed though)
    {
      updateCompanyStates(entityId);
    }

    setOpen(false);
  }, [entityId, showEditTimeSeries, updateCompanyStates]);

  const onOpen = useCallback(() => {
    setOpen(true);
  }, []);
  const isDisabled = !entityId || !customFieldId;

  return (
    <>
      <IconButton onClick={onOpen} size='small' title={'View time series'} disabled={isDisabled}>
        <TimelineIcon fontSize='small' />
      </IconButton>
      {open && (
        <BasicDialog
          open={open}
          onClose={onClose}
          lightDismiss={!showEditTimeSeries}
          title={`${formField.label} - Effective Dates`}
        >
          <Box height='50dvh' minHeight='50dvh' maxWidth='90dvw' width={'60rem'} p={'1rem 2.5rem'}>
            <GenericFallbacksWrapper>
              {showEditTimeSeries ? (
                <TimeSeriesDataTableEditable entityId={entityId ?? null} customFieldId={customFieldId} />
              ) : (
                <TimeSeriesDataTable entityId={entityId ?? null} customFieldId={customFieldId} />
              )}
            </GenericFallbacksWrapper>
          </Box>
        </BasicDialog>
      )}
    </>
  );
}

function useCustomFieldDataValues(key: string) {
  const methods = useFormContext();
  const entityId = useWatch({ name: 'id', control: methods?.control }) as number | undefined;
  const customFieldId = parseInt(key.split('.')[1]);

  return {
    entityId,
    customFieldId: isNaN(customFieldId) ? null : customFieldId,
  };
}
